import React, { useState, useRef } from "react";
import {
  Sliders,
  RotateCcw,
  RefreshCw,
  Check,
  Send,
  AlertCircle,
  FileUp,
  Loader2,
  Copy
} from "lucide-react";
import { DomainPreset } from "../types";
import { extractTextFromDocument } from "../lib/documentExtractor";

export interface TextEditorPanelProps {
  inputText: string;
  setInputText: (text: string) => void;
  handleDetectText: (e: React.FormEvent) => void;
  isLoading: boolean;
  error: string | null;
  setError: (err: string | null) => void;
  domainPreset: DomainPreset;
  handleDomainPresetChange: (preset: DomainPreset) => void;
  handleClearDocumentText: () => void;
  isClearing: boolean;
  handleDetectorPaste: (e: React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>) => void;
  isTranscribingPastedImage: boolean;
  wordCount: number;
  loadingSample: string | null;
  handleLoadSampleEssay: (type: "academic" | "ai") => void;
  estimateRealtimeHumanity: (text: string) => { score: number; label: string };
  onAddToast?: (type: "success" | "error" | "info" | "warning", title: string, message: string) => void;
  onOpenScoreGuide?: () => void;
}

export const TextEditorPanel: React.FC<TextEditorPanelProps> = ({
  inputText,
  setInputText,
  handleDetectText,
  isLoading,
  error,
  setError,
  domainPreset,
  handleDomainPresetChange,
  handleClearDocumentText,
  isClearing,
  handleDetectorPaste,
  isTranscribingPastedImage,
  wordCount,
  loadingSample,
  handleLoadSampleEssay,
  estimateRealtimeHumanity,
  onAddToast,
  onOpenScoreGuide
}) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isExtractingFile, setIsExtractingFile] = useState(false);
  const [extractingFileName, setExtractingFileName] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCopyText = async () => {
    if (!inputText.trim()) return;

    try {
      await navigator.clipboard.writeText(inputText);
      setIsCopied(true);
      if (onAddToast) {
        onAddToast("success", "Text Copied", "Document text copied to clipboard.");
      }
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      if (onAddToast) {
        onAddToast("error", "Copy Failed", "Could not copy text to clipboard.");
      }
    }
  };

  const readingTimeMinutes = Math.max(1, Math.ceil(wordCount / 200));

  const realtimeFlow = wordCount >= 5 ? estimateRealtimeHumanity(inputText).score : null;

  const handleProcessFile = async (file: File) => {
    setIsExtractingFile(true);
    setExtractingFileName(file.name);
    setError(null);

    try {
      const res = await extractTextFromDocument(file);
      if (res.success && res.text) {
        setInputText(res.text);
        if (onAddToast) {
          const typeLabel = res.fileType === "docx" ? "Word (.docx)" : res.fileType.toUpperCase();
          onAddToast(
            "success",
            "Document Ingested",
            `${res.wordCount.toLocaleString()} words imported from ${res.filename} (${typeLabel}).`
          );
        }
        if (res.warning && onAddToast) {
          onAddToast("info", "Formatting Flattened", res.warning);
        }
      } else {
        const errorMsg = res.error || "Failed to extract text from the document.";
        setError(errorMsg);
        if (onAddToast) {
          onAddToast("error", "File Read Error", errorMsg);
        }
      }
    } catch (err: any) {
      const errorMsg = err?.message || "An unexpected error occurred while parsing the document.";
      setError(errorMsg);
      if (onAddToast) {
        onAddToast("error", "Extraction Error", errorMsg);
      }
    } finally {
      setIsExtractingFile(false);
      setExtractingFileName(null);
      setIsDraggingOver(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingOver) setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only deactivate if leaving the container boundaries
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleProcessFile(files[0]);
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleProcessFile(files[0]);
    }
  };

  return (
    <form
      onSubmit={handleDetectText}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`bg-white dark:bg-slate-900 border transition-all duration-200 rounded-2xl p-4 sm:p-6 flex flex-col gap-4 shadow-xs max-w-full overflow-hidden relative ${
        isDraggingOver
          ? "border-slate-800 dark:border-slate-300 ring-4 ring-slate-900/5 dark:ring-white/5 bg-slate-50/80 dark:bg-slate-850/80"
          : "border-slate-200/90 dark:border-slate-800"
      }`}
      id="detector-input-container"
    >
      {/* Hidden File Input for Native File Picker */}
      <input
        ref={fileInputRef}
        type="file"
        aria-label="Upload document file"
        tabIndex={-1}
        className="hidden"
        accept=".docx,.pdf,.txt,.md,.markdown,.csv,.rtf,.json,image/*"
        onChange={handleFileInputChange}
      />

      {/* Header Bar: Clean, Responsive & Touch Friendly (Jakob's Law: QuillBot/Grammarly Model) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800/80 pb-3">
        <div className="flex items-center gap-2.5 min-w-0 flex-wrap">
          <h3 className="font-display font-bold text-slate-900 dark:text-white text-sm sm:text-base tracking-tight truncate hidden sm:inline">
            Document Review
          </h3>
          {/* Domain Preset Selector */}
          <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800/80 px-3 py-1.5 rounded-lg border border-slate-200/80 dark:border-slate-700 min-h-[38px]">
            <Sliders className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400 shrink-0" />
            <select
              value={domainPreset}
              onChange={(e) => handleDomainPresetChange(e.target.value as DomainPreset)}
              aria-label="Text type preset"
              className="text-xs font-semibold text-slate-800 dark:text-slate-200 bg-transparent focus:outline-hidden cursor-pointer"
            >
              <option value="general" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100">General Mode</option>
              <option value="academic" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100">Academic &amp; Essay</option>
              <option value="creative" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100">Creative Writing</option>
              <option value="technical" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100">Technical Report</option>
              <option value="business" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100">Business Prose</option>
            </select>
          </div>
        </div>

        {/* Top Utility Actions: Upload, Sample, Clear */}
        <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto flex-wrap">
          {/* Upload Document File Button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isExtractingFile}
            className="py-1.5 px-3 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white bg-slate-50 dark:bg-slate-850 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200/80 dark:border-slate-700 transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50 min-h-[38px] active:scale-98"
            title="Import Word (.docx), PDF, or text file"
            aria-label="Upload document file"
          >
            <FileUp className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
            <span>Upload File</span>
          </button>

          {/* Sample Text Quick Toggles */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 border border-slate-200/80 dark:border-slate-700 min-h-[38px]">
            <span className="text-[11px] text-slate-400 font-medium px-2 hidden md:inline">Sample:</span>
            <button
              type="button"
              onClick={() => handleLoadSampleEssay("academic")}
              disabled={loadingSample !== null || isExtractingFile}
              className="px-2.5 py-1 text-xs font-semibold rounded-md text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-700 transition-all cursor-pointer disabled:opacity-50"
              title="Load Human Writing Sample"
            >
              {loadingSample === "academic" ? "..." : "Human"}
            </button>
            <button
              type="button"
              onClick={() => handleLoadSampleEssay("ai")}
              disabled={loadingSample !== null || isExtractingFile}
              className="px-2.5 py-1 text-xs font-semibold rounded-md text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-700 transition-all cursor-pointer disabled:opacity-50"
              title="Load AI Generated Sample"
            >
              {loadingSample === "ai" ? "..." : "AI"}
            </button>
          </div>

          {/* Clear Button */}
          {inputText && (
            <button
              type="button"
              onClick={handleClearDocumentText}
              disabled={isClearing}
              className="py-1.5 px-3 rounded-lg text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 border border-transparent hover:border-rose-200 dark:hover:border-rose-900/50 transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-40 min-h-[38px] active:scale-98"
              title="Clear editor text"
              aria-label="Clear document text"
            >
              <RotateCcw className={`w-3.5 h-3.5 shrink-0 ${isClearing ? "animate-spin" : ""}`} />
              <span>{isClearing ? "Clearing..." : "Clear"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Primary Textarea Input with Drag Overlay & Transcribing State */}
      <div 
        className="relative group"
        onPaste={handleDetectorPaste}
      >
        <textarea
          id="pasted-text-area"
          value={inputText}
          onChange={(e) => { setInputText(e.target.value); if (error) setError(null); }}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
              e.preventDefault();
              if (!isLoading && wordCount >= 15) {
                handleDetectText(e);
              }
            }
          }}
          onPaste={handleDetectorPaste}
          placeholder="Paste, type, or drag and drop your document (.docx, .pdf, .txt, .md) here (minimum 15 words)..."
          className={`w-full min-h-[175px] sm:min-h-[220px] lg:min-h-[250px] max-h-[420px] border border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-950/40 rounded-xl p-4 sm:p-5 pr-12 sm:pr-14 text-base sm:text-base text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-hidden focus:border-slate-400 dark:focus:border-slate-500 focus:bg-white dark:focus:bg-slate-900 focus:ring-4 focus:ring-slate-900/5 dark:focus:ring-white/5 transition-all resize-y leading-relaxed font-sans ${
            isTranscribingPastedImage || isExtractingFile ? "opacity-40 cursor-wait pointer-events-none" : ""
          }`}
        />

        {/* Floating Copy Icon Button (Top-Right Inside Box, Icon Only, No Name) - Only shown when text is present */}
        {inputText.trim().length > 0 && (
          <button
            type="button"
            onClick={handleCopyText}
            className="absolute top-3 right-3 z-10 p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-white/90 dark:bg-slate-800/90 hover:bg-slate-100 dark:hover:bg-slate-750 border border-slate-200/80 dark:border-slate-700/80 shadow-xs backdrop-blur-xs transition-all cursor-pointer opacity-80 hover:opacity-100 active:scale-95 animate-fade-in"
            title={isCopied ? "Copied!" : "Copy text"}
            aria-label={isCopied ? "Copied to clipboard" : "Copy text to clipboard"}
            id="editor-copy-icon-btn"
          >
            {isCopied ? (
              <Check className="w-4 h-4 text-emerald-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        )}

        {/* Drag-and-Drop Active Overlay */}
        {isDraggingOver && (
          <div className="absolute inset-0 bg-slate-900/90 dark:bg-slate-950/92 backdrop-blur-md rounded-xl flex flex-col items-center justify-center gap-3 text-white z-20 pointer-events-none animate-fade-in p-6 text-center border-2 border-dashed border-slate-300 dark:border-slate-500">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-white">
              <FileUp className="w-6 h-6 animate-bounce" />
            </div>
            <div className="space-y-1">
              <p className="font-display font-semibold text-base">Drop your document here</p>
              <p className="text-xs text-slate-300">
                Directly extracts Word (.docx), PDF, Markdown (.md), and plain text (.txt)
              </p>
            </div>
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400 pt-1">
              <span className="px-2 py-0.5 rounded bg-white/10">.DOCX</span>
              <span className="px-2 py-0.5 rounded bg-white/10">.PDF</span>
              <span className="px-2 py-0.5 rounded bg-white/10">.TXT</span>
              <span className="px-2 py-0.5 rounded bg-white/10">.MD</span>
              <span className="px-2 py-0.5 rounded bg-white/10">IMAGES</span>
            </div>
          </div>
        )}

        {/* Processing State for Dropped/Selected Documents */}
        {isExtractingFile && (
          <div className="absolute inset-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xs rounded-xl flex flex-col items-center justify-center gap-3 text-slate-900 dark:text-white z-20 animate-fade-in p-6">
            <Loader2 className="w-8 h-8 animate-spin text-slate-800 dark:text-slate-200" />
            <div className="text-center space-y-1">
              <p className="font-semibold text-sm">
                Parsing {extractingFileName ? `"${extractingFileName}"` : "Document"}...
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Extracting textual structure and verifying character encoding
              </p>
            </div>
          </div>
        )}

        {/* Transcribing Overlay when screenshot is pasted */}
        {isTranscribingPastedImage && (
          <div className="absolute inset-0 bg-slate-900/30 dark:bg-slate-950/60 backdrop-blur-xs rounded-xl flex flex-col items-center justify-center gap-2 text-white z-10 animate-fade-in">
            <RefreshCw className="w-6 h-6 animate-spin text-white" />
            <span className="text-xs font-medium tracking-wide">Transcribing pasted screenshot...</span>
          </div>
        )}
      </div>

      {/* Goal Gradient: Dynamic Readiness Progress Bar */}
      <div className="flex flex-col gap-1.5 px-3 py-2 rounded-xl bg-slate-50/70 dark:bg-slate-850/50 border border-slate-100 dark:border-slate-800 transition-all">
        <div className="flex items-center justify-between text-xs gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 font-medium">
            {wordCount === 0 ? (
              <span className="text-slate-500 dark:text-slate-400">
                Step 1 of 2: Write, paste, or upload text to begin
              </span>
            ) : wordCount < 15 ? (
              <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1 font-semibold">
                <span>Step 1 of 2: Add {15 - wordCount} more word{15 - wordCount === 1 ? "" : "s"} to unlock analysis</span>
              </span>
            ) : wordCount < 50 ? (
              <span className="text-indigo-600 dark:text-indigo-400 flex items-center gap-1 font-semibold">
                <Check className="w-3.5 h-3.5 text-indigo-500" />
                <span>Step 2 of 2: Minimum reached &bull; Add {50 - wordCount} more word{50 - wordCount === 1 ? "" : "s"} for optimal precision</span>
              </span>
            ) : (
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                <Check className="w-3.5 h-3.5 text-emerald-500" />
                <span>100% Ready: Optimal statistical confidence</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 font-mono text-[11px] text-slate-500 dark:text-slate-400">
            <span>{wordCount.toLocaleString()} words</span>
            <span>&bull;</span>
            <span className="font-semibold text-slate-700 dark:text-slate-300">
              {wordCount >= 50 
                ? "100% Ready" 
                : wordCount >= 15 
                  ? `${Math.round(50 + ((wordCount - 15) / 35) * 50)}% Ready` 
                  : `${Math.round((wordCount / 15) * 50)}% Ready`}
            </span>
          </div>
        </div>

        {/* Dynamic Goal Gradient Progress Track */}
        <div 
          className="relative w-full h-1.5 bg-slate-200/80 dark:bg-slate-750 rounded-full overflow-hidden" 
          role="progressbar" 
          aria-label="Text analysis readiness progress"
          aria-valuenow={wordCount >= 50 ? 100 : Math.round((wordCount / 50) * 100)} 
          aria-valuemin={0} 
          aria-valuemax={100}
          aria-valuetext={wordCount >= 50 ? "100% Ready (50+ words)" : `${wordCount} of 50 recommended words`}
        >
          {/* Subtle 15-word threshold tick */}
          <div 
            className="absolute top-0 bottom-0 w-0.5 bg-slate-400/60 dark:bg-slate-500/60 z-10 pointer-events-none" 
            style={{ left: "30%" }} 
            title="15-word minimum threshold" 
          />
          <div 
            className={`h-full rounded-full transition-all duration-300 ease-out ${
              wordCount === 0 
                ? "w-0" 
                : wordCount < 15 
                  ? "bg-amber-500" 
                  : wordCount < 50 
                    ? "bg-indigo-500" 
                    : "bg-emerald-500 shadow-xs shadow-emerald-500/40"
            }`}
            style={{ 
              width: wordCount === 0 
                ? "0%" 
                : wordCount < 15 
                  ? `${Math.max(6, (wordCount / 15) * 30)}%` 
                  : `${30 + ((wordCount - 15) / 35) * 70}%` 
            }}
          />
        </div>
      </div>

      {/* Bottom Action Bar (Jakob's Law: QuillBot/Grammarly Anchored Primary Action Bar) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 pt-2 border-t border-slate-100 dark:border-slate-800/80">
        {/* Left: Unboxed Live Counters & Reading Time */}
        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 font-medium flex-wrap">
          <span className="font-mono tabular-nums font-semibold text-slate-800 dark:text-slate-200">
            {wordCount.toLocaleString()} {wordCount === 1 ? "word" : "words"}
          </span>
          <span className="text-slate-300 dark:text-slate-700" aria-hidden="true">·</span>
          <span className="font-mono tabular-nums text-slate-600 dark:text-slate-400">
            {inputText.length.toLocaleString()} characters
          </span>
          {wordCount > 0 && (
            <>
              <span className="text-slate-300 dark:text-slate-700" aria-hidden="true">·</span>
              <span className="text-slate-500 dark:text-slate-400">
                ~{readingTimeMinutes} min read
              </span>
            </>
          )}
          {wordCount > 0 && wordCount < 15 ? (
            <>
              <span className="text-slate-300 dark:text-slate-700" aria-hidden="true">·</span>
              <span className="text-amber-600 dark:text-amber-400 font-semibold">
                Needs {15 - wordCount} more word{15 - wordCount === 1 ? "" : "s"}
              </span>
            </>
          ) : wordCount >= 15 ? (
            <>
              <span className="text-slate-300 dark:text-slate-700" aria-hidden="true">·</span>
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold inline-flex items-center gap-1">
                <Check className="w-3.5 h-3.5" />
                Ready to scan
              </span>
            </>
          ) : null}
        </div>

        {/* Right: Primary Scan Button - High-Contrast Prominent CTA with Hotkey */}
        <button
          type="submit"
          disabled={isLoading || wordCount < 15 || isExtractingFile}
          className={`w-full sm:w-auto py-2.5 px-6 min-h-[44px] rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2.5 cursor-pointer shrink-0 shadow-sm ${
            wordCount >= 15 && !isLoading
              ? "bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 active:scale-[0.98] ring-2 ring-slate-900/10 dark:ring-white/10"
              : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500 cursor-not-allowed opacity-60"
          }`}
          id="analyze-document-btn"
          aria-label={isLoading ? "Detecting AI..." : "Detect AI Text"}
        >
          {isLoading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Detecting AI...</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>Detect AI Text</span>
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-300 text-xs rounded-xl p-3 border border-rose-200 dark:border-rose-900/50 flex items-start gap-2.5 mt-1">
          <AlertCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </form>
  );
};
